import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  LocationIdSchema,
  RmAutoReplyDelaySchema,
  RmAutoReplyReviewRestrictionSchema,
  RmAutoReplyRuleIdSchema,
  REPORT_SCHEDULE,
  RmBreakdownIntervalSchema,
  RmDirectoryIdentifierSchema,
  RmEmailAlertModeSchema,
  RmProfileLookupIdSchema,
  RmProfileLookupStatusSchema,
  RmRatingBucketSchema,
  RmReplyTemplateIdSchema,
  RmReportIdSchema,
  RmReviewGradeSchema,
  RmReviewIdSchema,
  RmReviewRatingFilterSchema,
  RmReviewStatusSchema,
  RmReviewTypeSchema,
  RmStarRatingSchema,
  RmTemplateRatingTypeSchema,
  RM_EMAIL_ALERT_MODE,
  RM_RATING_BUCKET,
  RM_REVIEW_GRADE,
  RM_REVIEW_STATUS,
  RM_STAR_RATING,
  RM_TEMPLATE_RATING_TYPE
} from '@brightlocal/shared';
import type {
  LocationId,
  RmAutoReplyRuleId,
  RmBreakdownInterval,
  RmBreakdownType,
  RmEmailAlertMode,
  RmRatingBucket,
  RmReportId,
  RmReviewGrade,
  RmStarRating,
  RmTemplateRatingType
} from '@brightlocal/shared';

import { PaginationSchema } from '../../../schemas';

// The API spells some values two ways while it switches over. The response schemas also read the other spelling
// and return the dictionary value; the request schemas send only the dictionary value.
// TODO: accept only the dictionary spellings once the API sends nothing else, then delete these aliases
// [https://brightlocal.atlassian.net/browse/FP-671]

/** The dictionary value of each reply template rating in its other spelling. */
const RM_TEMPLATE_RATING_TYPE_ALIASES = {
  allRatings: RM_TEMPLATE_RATING_TYPE.ALL_RATINGS,
  star1: RM_TEMPLATE_RATING_TYPE.STAR_1,
  star2: RM_TEMPLATE_RATING_TYPE.STAR_2,
  star3: RM_TEMPLATE_RATING_TYPE.STAR_3,
  star4: RM_TEMPLATE_RATING_TYPE.STAR_4,
  star5: RM_TEMPLATE_RATING_TYPE.STAR_5,
  'not-recommended': RM_TEMPLATE_RATING_TYPE.NOT_RECOMMENDED
} as const satisfies Record<string, RmTemplateRatingType>;

const RMTemplateRatingTypeAliasSchema = z
  .enum(['allRatings', 'star1', 'star2', 'star3', 'star4', 'star5', 'not-recommended'])
  .transform((alias) => RM_TEMPLATE_RATING_TYPE_ALIASES[alias]);

/** The dictionary value of each auto-reply rule rating in its other spelling. */
const RM_AUTO_REPLY_RATING_ALIASES = {
  star1: RM_STAR_RATING.ONE,
  star2: RM_STAR_RATING.TWO,
  star3: RM_STAR_RATING.THREE,
  star4: RM_STAR_RATING.FOUR,
  star5: RM_STAR_RATING.FIVE
} as const satisfies Record<string, RmStarRating>;

const RMAutoReplyRatingAliasSchema = z
  .enum(['star1', 'star2', 'star3', 'star4', 'star5'])
  .transform((alias) => RM_AUTO_REPLY_RATING_ALIASES[alias]);

/** The dictionary value of each rating bucket in its other spelling. */
const RM_RATING_BUCKET_ALIASES = {
  'not-recommended': RM_RATING_BUCKET.NOT_RECOMMENDED
} as const satisfies Record<string, RmRatingBucket>;

const RMRatingBucketAliasSchema = z.enum(['not-recommended']).transform((alias) => RM_RATING_BUCKET_ALIASES[alias]);

/** The dictionary value of each breakdown cell rating in its other spelling: a star rating as its bucket key, a string. */
const RM_BREAKDOWN_RATING_ALIASES = {
  [RM_RATING_BUCKET.FIVE]: RM_STAR_RATING.FIVE,
  [RM_RATING_BUCKET.FOUR]: RM_STAR_RATING.FOUR,
  [RM_RATING_BUCKET.THREE]: RM_STAR_RATING.THREE,
  [RM_RATING_BUCKET.TWO]: RM_STAR_RATING.TWO,
  [RM_RATING_BUCKET.ONE]: RM_STAR_RATING.ONE,
  'not-recommended': RM_REVIEW_GRADE.NOT_RECOMMENDED
} as const satisfies Record<string, RmStarRating | RmReviewGrade>;

const RMBreakdownRatingAliasSchema = z
  .enum([
    RM_RATING_BUCKET.FIVE,
    RM_RATING_BUCKET.FOUR,
    RM_RATING_BUCKET.THREE,
    RM_RATING_BUCKET.TWO,
    RM_RATING_BUCKET.ONE,
    'not-recommended'
  ])
  .transform((alias) => RM_BREAKDOWN_RATING_ALIASES[alias]);

/** The dictionary value of each email alert mode in its other spelling. */
const RM_EMAIL_ALERT_MODE_ALIASES = {
  'no-alerts': RM_EMAIL_ALERT_MODE.NO_ALERTS,
  'on-new-reviews': RM_EMAIL_ALERT_MODE.ON_NEW_REVIEWS
} as const satisfies Record<string, RmEmailAlertMode>;

const RMEmailAlertModeAliasSchema = z
  .enum(['no-alerts', 'on-new-reviews'])
  .transform((alias) => RM_EMAIL_ALERT_MODE_ALIASES[alias]);

/** A directory on a new report and whether the report collects reviews from it. */
export const RMCreateReportDirectorySchema = z.object({
  /** Directory identifier, as returned by Get Available Directories. */
  name: z.string(),
  use: z.boolean()
});

/** Body of Create Report. */
export const RMCreateReportSettingsSchema = z.object({
  location_id: LocationIdSchema,
  schedule: z.nativeEnum(REPORT_SCHEDULE),
  /** ISO weekday for weekly, day of month (1-28, or -1 for the last day) for monthly; unused otherwise. */
  run_on: z.number().int().optional(),
  email_alert_mode: RmEmailAlertModeSchema,
  /** Finished lookups the directories' profiles come from; every directory switched on must be covered by one. */
  profile_lookup_ids: z.array(RmProfileLookupIdSchema).optional(),
  /** Google, Facebook and Yelp are collected from whenever the location is connected to them, listed or not. */
  directories: z.array(RMCreateReportDirectorySchema).optional()
});

/** Create Report answers with the id of the new report. */
export const RMCreateReportResponseSchema = z.object({
  id: RmReportIdSchema
});

/** Body of Create Profile Lookup. */
export const RMCreateProfileLookupSettingsSchema = z.object({
  location_id: LocationIdSchema,
  /** Identifiers of the directories to search, as returned by Get Available Directories; at least one. */
  directories: z.array(z.string()).min(1)
});

/** Create Profile Lookup answers with the id of the queued lookup. */
export const RMCreateProfileLookupResponseSchema = z
  .object({
    lookup_id: RmProfileLookupIdSchema,
    created_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** What a profile lookup found on one directory. */
export const RMProfileLookupDirectorySchema = z.object({
  /** Profile URL found on the directory; `null` until it is searched, and when no profile was found. */
  url: z.string().nullable(),
  /** Whether the directory was part of the lookup. */
  use: z.boolean(),
  /** Whether the directory has been searched; `url` means nothing until it has. */
  searched: z.boolean()
});

/**
 * A profile lookup and what it has found so far. `directories` holds single-entry objects keyed by
 * directory identifier, so only top-level keys are camelCased.
 */
export const RMProfileLookupSchema = z
  .object({
    lookup_id: RmProfileLookupIdSchema,
    status: RmProfileLookupStatusSchema,
    directories: z.array(z.record(z.string(), RMProfileLookupDirectorySchema))
  })
  .transform((data) => camelcaseKeys(data));

/** Body of Create Report. */
export type RMCreateReportSettings = z.infer<typeof RMCreateReportSettingsSchema>;
/** Body of Create Profile Lookup. */
export type RMCreateProfileLookupSettings = z.infer<typeof RMCreateProfileLookupSettingsSchema>;
/** Create Profile Lookup answers with the id of the queued lookup. */
export type RMCreateProfileLookupResponse = z.infer<typeof RMCreateProfileLookupResponseSchema>;
/** What a profile lookup found on one directory. */
export type RMProfileLookupDirectory = z.infer<typeof RMProfileLookupDirectorySchema>;
/** A profile lookup and what it has found so far. */
export type RMProfileLookup = z.infer<typeof RMProfileLookupSchema>;

/** Query params of Get Review Performance. `from` and `to` are RFC 3339 date-times, both inclusive. */
export interface RMPerformanceParams {
  /** The report whose reviews are read. */
  reportId: RmReportId;
  /** Earliest review date; the API starts from the report's first review when omitted. */
  from?: string;
  /** Latest review date; the API runs up to the most recent review when omitted. */
  to?: string;
}

/** Query params of Get Review Directory Breakdown. */
export interface RMDirectoryBreakdownParams extends RMPerformanceParams {
  /** The API defaults to `monthly` when omitted. */
  interval?: RmBreakdownInterval;
  /** The API defaults to `all` when omitted. */
  type?: RmBreakdownType;
}

const RMSourcePerformanceSchema = z.object({
  total: z.number(),
  ratings: z.record(RmRatingBucketSchema.or(RMRatingBucketAliasSchema), z.number())
});

/**
 * Per-source figures keyed by directory identifier. A report that collects from no source gets `[]`,
 * because the backend serialises an empty map as an empty array, so both shapes mean "no sources".
 */
export const RMSourcesSchema = z.union([z.record(RmDirectoryIdentifierSchema, RMSourcePerformanceSchema), z.tuple([])]);

/**
 * Review totals of a report within a period. Source identifiers and rating buckets keep their raw keys,
 * so only top-level keys are camelCased.
 */
export const RMPerformanceSchema = z
  .object({
    total_reviews: z.number(),
    average_rating: z.number().nullable(),
    sources: RMSourcesSchema
  })
  .transform((data) => camelcaseKeys(data));

/** Review counts of a report as a period × source × rating grid, flattened into cells. */
export const RMDirectoryBreakdownResponseSchema = z.object({
  data: z.object({
    interval: RmBreakdownIntervalSchema,
    breakdown: z.array(
      z.object({
        /** First day of the period, a plain date. */
        date: z.string(),
        source: RmDirectoryIdentifierSchema,
        /** A star rating as an integer, or a recommendation grade. */
        rating: RmStarRatingSchema.or(RmReviewGradeSchema).or(RMBreakdownRatingAliasSchema),
        count: z.number()
      })
    )
  })
});

/** Per-report settings of one directory, keyed by its identifier in `RMReport.directories`. */
export const RMReportDirectorySchema = z.object({
  /** Business profile URL reviews are collected from; empty when no profile is set. */
  url: z.string().optional(),
  /** Whether the directory is included in the report. */
  use: z.boolean().optional(),
  /** Whether BrightLocal has searched this directory for the business profile. */
  searched: z.boolean().optional()
});

/**
 * A Reputation Manager report. `directories` holds single-entry objects keyed by directory identifier,
 * so only top-level keys are camelCased.
 */
export const RMReportSchema = z
  .object({
    id: RmReportIdSchema,
    report_name: z.string().optional(),
    location_id: LocationIdSchema,
    email_alert_mode: RmEmailAlertModeSchema.or(RMEmailAlertModeAliasSchema).optional(),
    email_alert_addresses: z.array(z.string()).optional(),
    schedule: z.nativeEnum(REPORT_SCHEDULE).optional(),
    /** ISO weekday for daily/weekly, day of month (-1 = last day) for monthly, null for adhoc. */
    run_on: z.number().nullish(),
    directories: z.array(z.record(RmDirectoryIdentifierSchema, RMReportDirectorySchema)).optional(),
    created_at: z.string(),
    updated_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** A directory Reputation Manager can collect reviews from for a location. */
export const RMAvailableDirectorySchema = z
  .object({
    /** Base URL of the directory. */
    base_url: z.string(),
    /** Identifier used in report directory settings and in performance and breakdown `source` keys. */
    identifier: RmDirectoryIdentifierSchema,
    /** Display name of the directory. */
    name: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** Directories available for a location. */
export const RMAvailableDirectoriesResponseSchema = z.object({
  data: z.array(RMAvailableDirectorySchema)
});

/** Run Report answers with an empty body. */
export const RMRunReportResponseSchema = z.undefined();

/** Paging fields every RM v2 search body takes. */
const RMSearchPageParamsSchema = z.object({
  /** 1-indexed; the API defaults to 1. */
  page: z.number().int().optional(),
  /** At most 100; the API defaults to 25. */
  num_per_page: z.number().int().optional()
});

/** Sort direction every RM v2 search body takes. */
const RMSortOrderSchema = z.enum(['asc', 'desc']);

/** Body of Find Reviews. Every field is optional; an omitted filter matches every value. */
export const RMFindReviewsParamsSchema = RMSearchPageParamsSchema.extend({
  /** Omitted means newest first; `order` defaults to `asc` when only `field` is sent. */
  sort: z
    .object({
      field: z.enum(['published_at', 'rating']),
      order: RMSortOrderSchema.optional()
    })
    .optional(),
  /** Narrows the search; every filter set must match. */
  filter: z
    .object({
      /** Only reviews collected by this report; omitted means every report in the account. */
      report_id: RmReportIdSchema.optional(),
      /** Free text matched against the review, its author and its report; at least 1 character, omit it to search everything. */
      query: z.string().min(1).optional(),
      /** Only reviews in one of these workflow statuses; at least one, omit it for every status. */
      status: z.array(RmReviewStatusSchema).min(1).optional(),
      /** RFC 3339 date-times, both bounds inclusive. */
      published_at: z.object({ from: z.string().optional(), to: z.string().optional() }).optional(),
      /** Star ratings as integers, recommendation grades, or `no_rating`; at least one, omit it for every rating. */
      rating: z.array(RmReviewRatingFilterSchema).min(1).optional(),
      /** Directory identifiers, as returned by Get Available Directories; at least one, omit it for every directory. */
      review_directory: z.array(RmDirectoryIdentifierSchema).min(1).optional()
    })
    .optional()
});

/**
 * A review collected by a Reputation Manager report. A `review` has a null `grade`, and a star `rating`
 * unless the directory published it without one, when `rating` is null too. A `recommendation` has a
 * `grade` and a null `rating`.
 */
export const RMReviewSchema = z
  .object({
    review_id: RmReviewIdSchema,
    /** The report that collected the review. */
    report_id: RmReportIdSchema,
    /** Display name of that report. */
    report_name: z.string(),
    /** Identifier of the directory the review came from, e.g. `google`. */
    directory: RmDirectoryIdentifierSchema,
    /** Display name of that directory. */
    directory_name: z.string(),
    /** Workflow status of the review in the inbox. */
    status: RmReviewStatusSchema,
    /** Whether a reply can be posted to the review through BrightLocal. */
    can_respond: z.boolean(),
    /** `review` carries a star rating, `recommendation` a grade. */
    type: RmReviewTypeSchema,
    /** Empty when the directory publishes the review anonymously. */
    author: z.string(),
    /** Empty when the reviewer left only a rating. */
    text: z.string(),
    /** 1 to 5; null for a recommendation or a review with no star rating. */
    rating: z.number().nullable(),
    /** Set for a recommendation; null for a review. */
    grade: RmReviewGradeSchema.nullable(),
    /** Empty when the directory does not expose a link. */
    link: z.string(),
    /** When the report run that collected the review took place; null for a review not attributed to a run. */
    fetched_at: z.string().nullable(),
    /** When the directory published the review. */
    published_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** A page of Find Reviews results. */
export const RMFindReviewsResponseSchema = z.object({
  data: z.array(RMReviewSchema),
  pagination: PaginationSchema
});

/** Body of Update Review: the four statuses the review inbox moves a review between. */
export const RMUpdateReviewSettingsSchema = z.object({
  status: z.enum([
    RM_REVIEW_STATUS.UNREAD,
    RM_REVIEW_STATUS.ACTION_REQUIRED,
    RM_REVIEW_STATUS.DO_NOT_RESPOND,
    RM_REVIEW_STATUS.MANUALLY_RESPONDED
  ])
});

/** The reply posted to a review, by hand or by an auto-reply rule. */
export const RMReviewReplySchema = z
  .object({
    review_id: RmReviewIdSchema,
    /** Null when the posting time was never recorded. */
    responded_at: z.string().nullable(),
    response: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** Body of Respond to Review. */
export const RMRespondToReviewSettingsSchema = z.object({
  /** Up to 4000 characters; may use the `{{FirstName}}` and `{{BusinessName}}` placeholders. */
  content: z.string()
});

/** A suggested reply; nothing is posted or saved. */
export const RMGeneratedReviewReplySchema = z.object({
  content: z.string()
});

/** Body of Create Reply Template. */
export const RMCreateReplyTemplateSettingsSchema = z.object({
  /** 3 to 30 characters; how the template is listed, never shown to the reviewer. */
  name: z.string().min(3).max(30),
  /** 3 to 350 characters; may use the `{{FirstName}}` and `{{BusinessName}}` placeholders. */
  content: z.string().min(3).max(350),
  /** The API stores `all_ratings` when omitted. */
  review_rating_type: RmTemplateRatingTypeSchema.optional(),
  /** Client ids the template is restricted to, at most 10; omitted or empty means the whole account. */
  clients: z.array(z.number()).max(10).optional(),
  /** The API defaults to `true`. */
  is_allowed_for_auto_reply: z.boolean().optional()
});

/** Create Reply Template answers with the id of the new template. */
export const RMCreateReplyTemplateResponseSchema = z.object({
  id: RmReplyTemplateIdSchema
});

/** Body of Update Reply Template. Every field is optional, and a field left out keeps its value. */
export const RMUpdateReplyTemplateSettingsSchema = z.object({
  /** 3 to 30 characters; how the template is listed, never shown to the reviewer. */
  name: z.string().min(3).max(30).optional(),
  /** 3 to 350 characters; may use the `{{FirstName}}` and `{{BusinessName}}` placeholders. */
  content: z.string().min(3).max(350).optional(),
  /** Send `all_ratings` to lift a rating restriction the template has. */
  review_rating_type: RmTemplateRatingTypeSchema.optional(),
  /** Replaces the client ids the template is restricted to, at most 10; `[]` opens it to the whole account. */
  clients: z.array(z.number()).max(10).optional(),
  /** Whether an auto-reply rule may use the template. */
  is_allowed_for_auto_reply: z.boolean().optional()
});

/** Body of Find Reply Templates. Omitted `sort` means by name, A to Z. */
export const RMFindReplyTemplatesParamsSchema = RMSearchPageParamsSchema.extend({
  /** `order` defaults to `asc` when only `field` is sent. */
  sort: z.object({ field: z.enum(['name']), order: RMSortOrderSchema.optional() }).optional(),
  /** Narrows the search; every filter set must match. */
  filter: z
    .object({
      /** Free text matched against the template's name and content; at least 1 character, omit it to search everything. */
      query: z.string().min(1).optional(),
      /** Exact match on the stored value; at least one, omit it for every rating type. */
      review_rating_type: z.array(RmTemplateRatingTypeSchema).min(1).optional()
    })
    .optional()
});

/** A reusable reply to a review. `content` keeps its placeholders unreplaced. */
export const RMReplyTemplateSchema = z
  .object({
    id: RmReplyTemplateIdSchema,
    /** How the template is listed when choosing one; never shown to the reviewer. */
    name: z.string(),
    /** Body of the reply, with `{{FirstName}}` and `{{BusinessName}}` kept as written. */
    content: z.string(),
    /** Which reviews the template is offered for; `all_ratings` when there is no restriction. */
    review_rating_type: RmTemplateRatingTypeSchema.or(RMTemplateRatingTypeAliasSchema),
    /** Client ids the template is restricted to; empty when it is available across the whole account. */
    clients: z.array(z.number()),
    /** Whether an auto-reply rule may use the template. */
    is_allowed_for_auto_reply: z.boolean(),
    /** When the template was created. */
    created_at: z.string(),
    /** Null for a template never changed since it was created. */
    updated_at: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

/** A page of Find Reply Templates results. */
export const RMFindReplyTemplatesResponseSchema = z.object({
  data: z.array(RMReplyTemplateSchema),
  pagination: PaginationSchema
});

/** The Google star ratings an auto-reply rule reacts to. Google is the only platform. */
const RMAutoReplyRuleRatingsSchema = z.object({
  /** Directory identifier of the review platform; only `google` is supported. */
  platform: z.enum(['google']),
  /**
   * The star ratings on that platform the rule replies to; at least one. Each is an exact rating: `3` means three
   * stars only, unlike a reply template's threshold.
   */
  values: z.array(RmStarRatingSchema).min(1)
});

/** Body of Find Auto-Reply Rules. Every field is optional; an omitted `sort` means newest first. */
export const RMFindAutoReplyRulesParamsSchema = RMSearchPageParamsSchema.extend({
  /** `order` defaults to `asc` when only `field` is sent. */
  sort: z.object({ field: z.enum(['name', 'created_at']), order: RMSortOrderSchema.optional() }).optional(),
  /** Narrows the search; every filter set must match. */
  filter: z
    .object({
      /** Free text matched against the rule name. */
      query: z.string().optional(),
      /** Only rules that include this location. */
      location_id: LocationIdSchema.optional(),
      /** Only rules that react to any of these ratings. */
      rating: z.array(RmStarRatingSchema).optional()
    })
    .optional()
});

/** A rule that replies to Google reviews automatically, with an enabled flag per location. */
export const RMAutoReplyRuleSchema = z
  .object({
    id: RmAutoReplyRuleIdSchema,
    /** Display name of the rule. */
    name: z.string(),
    /** Minutes to wait after a review is collected before the reply is posted. */
    delay_minutes: RmAutoReplyDelaySchema,
    /** The locations the rule applies to, each with whether the rule currently replies there. */
    locations: z.array(z.object({ id: LocationIdSchema, is_enabled: z.boolean() })),
    /** The ratings the rule reacts to, per platform. */
    ratings: z.array(
      RMAutoReplyRuleRatingsSchema.extend({
        /** The star ratings on that platform the rule replies to; at least one. */
        values: z.array(RmStarRatingSchema.or(RMAutoReplyRatingAliasSchema)).min(1)
      })
    ),
    /** Replies to reviews with text, without text, or both. */
    review_restrictions: z.array(RmAutoReplyReviewRestrictionSchema),
    /** The reply templates the rule chooses from. */
    template_ids: z.array(RmReplyTemplateIdSchema),
    /** When the rule was created. */
    created_at: z.string(),
    /** When the rule was last updated. */
    updated_at: z.string()
  })
  .transform((data) => camelcaseKeys(data, { deep: true }));

/** A page of Find Auto-Reply Rules results. */
export const RMFindAutoReplyRulesResponseSchema = z.object({
  data: z.array(RMAutoReplyRuleSchema),
  pagination: PaginationSchema
});

/** Body of Create Auto-Reply Rule. The rule starts enabled for every location it names. */
export const RMCreateAutoReplyRuleSettingsSchema = z.object({
  /** 3 to 100 characters. */
  name: z.string().min(3).max(100),
  /** Minutes to wait after a review is collected before the reply is posted. */
  delay_minutes: RmAutoReplyDelaySchema,
  /** 1 to 100 locations of the account. */
  location_ids: z.array(LocationIdSchema).min(1).max(100),
  /** The ratings the rule reacts to, per platform; at least one. */
  ratings: z.array(RMAutoReplyRuleRatingsSchema).min(1),
  /** At least one; send both to reply to every review. */
  review_restrictions: z.array(RmAutoReplyReviewRestrictionSchema).min(1),
  /** 1 to 100 templates of the account that allow automatic replies. */
  template_ids: z.array(RmReplyTemplateIdSchema).min(1).max(100)
});

/** Create Auto-Reply Rule answers with the id of the new rule. */
export const RMCreateAutoReplyRuleResponseSchema = z.object({
  id: RmAutoReplyRuleIdSchema
});

/** Path of Update Auto-Reply Rule Location: the rule and one of its locations. */
export interface RMAutoReplyRuleLocationParams {
  /** The rule to switch. */
  ruleId: RmAutoReplyRuleId;
  /** One of the rule's locations. */
  locationId: LocationId;
}

/** Body of Update Auto-Reply Rule Location. */
export const RMUpdateAutoReplyRuleLocationSettingsSchema = z.object({
  /** Whether the rule replies to this location's reviews. */
  is_enabled: z.boolean()
});

/** Per-source figures of a review performance response, or `[]` for a report with no source. */
export type RMSources = z.infer<typeof RMSourcesSchema>;
/** Review totals of a report within a period. */
export type RMPerformance = z.infer<typeof RMPerformanceSchema>;
/** Review counts grid of a report. */
export type RMDirectoryBreakdownResponse = z.infer<typeof RMDirectoryBreakdownResponseSchema>;
/** Per-report settings of one directory. */
export type RMReportDirectory = z.infer<typeof RMReportDirectorySchema>;
/** A Reputation Manager report. */
export type RMReport = z.infer<typeof RMReportSchema>;
/** A directory available for a location. */
export type RMAvailableDirectory = z.infer<typeof RMAvailableDirectorySchema>;
/** Body of Find Reviews. */
export type RMFindReviewsParams = z.infer<typeof RMFindReviewsParamsSchema>;
/** A review collected by a Reputation Manager report. */
export type RMReview = z.infer<typeof RMReviewSchema>;
/** A page of Find Reviews results. */
export type RMFindReviewsResponse = z.infer<typeof RMFindReviewsResponseSchema>;
/** Body of Update Review. */
export type RMUpdateReviewSettings = z.infer<typeof RMUpdateReviewSettingsSchema>;
/** The reply posted to a review. */
export type RMReviewReply = z.infer<typeof RMReviewReplySchema>;
/** Body of Respond to Review. */
export type RMRespondToReviewSettings = z.infer<typeof RMRespondToReviewSettingsSchema>;
/** A suggested reply to a review. */
export type RMGeneratedReviewReply = z.infer<typeof RMGeneratedReviewReplySchema>;
/** Body of Create Reply Template. */
export type RMCreateReplyTemplateSettings = z.infer<typeof RMCreateReplyTemplateSettingsSchema>;
/** Body of Update Reply Template. */
export type RMUpdateReplyTemplateSettings = z.infer<typeof RMUpdateReplyTemplateSettingsSchema>;
/** Body of Find Reply Templates. */
export type RMFindReplyTemplatesParams = z.infer<typeof RMFindReplyTemplatesParamsSchema>;
/** A reusable reply to a review. */
export type RMReplyTemplate = z.infer<typeof RMReplyTemplateSchema>;
/** A page of Find Reply Templates results. */
export type RMFindReplyTemplatesResponse = z.infer<typeof RMFindReplyTemplatesResponseSchema>;
/** Body of Find Auto-Reply Rules. */
export type RMFindAutoReplyRulesParams = z.infer<typeof RMFindAutoReplyRulesParamsSchema>;
/** A rule that replies to reviews automatically. */
export type RMAutoReplyRule = z.infer<typeof RMAutoReplyRuleSchema>;
/** A page of Find Auto-Reply Rules results. */
export type RMFindAutoReplyRulesResponse = z.infer<typeof RMFindAutoReplyRulesResponseSchema>;
/** Body of Create Auto-Reply Rule. */
export type RMCreateAutoReplyRuleSettings = z.infer<typeof RMCreateAutoReplyRuleSettingsSchema>;
/** Body of Update Auto-Reply Rule Location. */
export type RMUpdateAutoReplyRuleLocationSettings = z.infer<typeof RMUpdateAutoReplyRuleLocationSettingsSchema>;
/** Directories available for a location. */
export type RMAvailableDirectoriesResponse = z.infer<typeof RMAvailableDirectoriesResponseSchema>;
